/**
 * Zoom limits for the workflow builder canvas.
 *
 * ReactFlow's min/max zoom are locked in once, after the graph has been fitted
 * to the viewport, so both bounds are expressed relative to that fitted zoom.
 */

/** Desktop zoom-in ceiling, as a multiple of the fitted zoom. */
const DESKTOP_MAX_ZOOM_FACTOR = 1.4;

/**
 * Mobile zoom-in ceiling, as a multiple of the fitted zoom. Larger than
 * desktop because a narrow viewport fits a wide graph at a much lower zoom.
 */
const MOBILE_MAX_ZOOM_FACTOR = 4;

/**
 * Absolute lower bound on the mobile ceiling. A wide workflow can fit at ~0.15
 * zoom on a phone, where even 4x is only 0.6 — too small to read a node label.
 * This guarantees pinch-to-zoom can always reach a legible scale.
 */
const MOBILE_MIN_ZOOM_CEILING = 1.5;

export interface ZoomRange {
  min: number;
  max: number;
}

/**
 * computeZoomRange derives the canvas zoom bounds from the zoom that fitView
 * settled on.
 *
 * The fitted zoom is the floor on every viewport: the whole graph is already
 * visible there, so pulling back further only shrinks it into the middle of an
 * empty canvas. Mobile differs only in the ceiling, because a narrow viewport
 * fits a wide graph too small to read.
 */
export function computeZoomRange(fittedZoom: number, isMobile: boolean): ZoomRange {
  const max = isMobile
    ? Math.max(fittedZoom * MOBILE_MAX_ZOOM_FACTOR, MOBILE_MIN_ZOOM_CEILING)
    : fittedZoom * DESKTOP_MAX_ZOOM_FACTOR;
  return { min: fittedZoom, max };
}
